import { getLogger } from '@logtape/logtape';

import useAppointmentsForDate from '@/hooks/useAppointmentsForDate.ts';
import useCheckInData from '@/hooks/useCheckInData.ts';
import { revalidateAppointmentFormsCache } from '@/hooks/usePatientAppointmentForms.ts';

import { ColumnType, KanbanColumn } from '@/models/RoomView.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { AppointmentMoveOutcome } from '@/models/appointment/appointment-move-outcome.ts';
import { CheckinIntent, CheckinOutcome, CheckinOutcomeKind } from '@/models/call-system/check-in.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { InsuranceType } from '@/models/insurance.ts';

import {
    checkin,
    checkoutAppointment,
    prepareAndCheckinArrived,
    reportCheckinCompletion,
    updateAppointment,
    updateAppointmentInsuranceType,
} from '@/services/appointmentService.ts';
import { getRequiredAppointmentForms } from '@/services/formDataService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { useAppointmentCheckinStore } from '@/stores/appointmentCheckinStore.ts';

import { appendPatientContext, getRoomId } from '@/utils/appointmentUtils.ts';
import { getSelectedLocation } from '@/utils/configUtils.ts';
import {
    type OnFailOptions,
    handleAppointmentMove,
    handleCheckinMove,
    moveFailedMessage,
} from '@/utils/roomViewUtils.ts/dragAndDrop.ts';

const logger = getLogger(['hook', 'check-in-actions']);

const ROOM_CHANGE_ERROR = 'Ein Problem ist aufgetreten, als der Patient in den Raum verlegt wurde.'; // A problem occurred while moving the patient to the room.
const CHECKIN_ERROR = 'Beim Einchecken ist ein Problem aufgetreten'; // A problem occurred during check-in.

export interface CheckInActionCallbacks {
    onSuccess?: () => void;
    onFail?: () => void;
    onRoomChanged?: (room: string) => void;
    /** Called after the insurance type has been persisted so the caller can update local UI state. */
    onInsuranceUpdated?: (updatedAppointment: DailyAppointment) => void;
}

interface ArriveParams {
    appointment: DailyAppointment;
    room: string;
    checkinData: UserAppointmentDto | undefined;
    enrichedPractitionerData: EnrichedPractitionerData | undefined;
    forms: FormattedForm[] | undefined;
}

interface ArriveWithFormsParams {
    appointment: DailyAppointment;
    room: string;
    enrichedPractitionerData: EnrichedPractitionerData | undefined;
    forms: FormattedForm[] | undefined;
}

interface ChangeRoomParams {
    appointment: DailyAppointment;
    room: string;
}

interface ConfirmArrivedParams {
    appointment: DailyAppointment;
    room: string;
    checkinData: UserAppointmentDto | undefined;
}

interface CheckOutParams {
    appointment: DailyAppointment;
    checkinData: UserAppointmentDto | undefined;
}

interface ArriveWithInsuranceUpdateParams {
    appointment: DailyAppointment;
    room: string;
    insuranceType: InsuranceType;
    checkinData: UserAppointmentDto | undefined;
    enrichedPractitionerData: EnrichedPractitionerData | undefined;
}

interface DragMoveParams {
    appointment: DailyAppointment;
    sourceColumn: KanbanColumn<ColumnType, DailyAppointment>;
    destinationColumn: KanbanColumn<ColumnType, DailyAppointment>;
    checkinData: UserAppointmentDto | undefined;
    enrichedPractitionerData: EnrichedPractitionerData | undefined;
}

interface DragMoveCallbacks {
    onSettled: () => void;
    onReverted: () => void;
    acquireCheckinLock: () => void;
}

export interface CheckInActions {
    arriveAndCheckIn: (params: ArriveParams, callbacks?: CheckInActionCallbacks) => Promise<void>;
    arriveWithForms: (params: ArriveWithFormsParams, callbacks?: CheckInActionCallbacks) => Promise<void>;
    arriveWithInsuranceUpdate: (
        params: ArriveWithInsuranceUpdateParams,
        callbacks?: CheckInActionCallbacks
    ) => Promise<void>;
    changeRoom: (params: ChangeRoomParams, callbacks?: CheckInActionCallbacks) => Promise<void>;
    confirmArrived: (params: ConfirmArrivedParams, callbacks?: CheckInActionCallbacks) => Promise<void>;
    checkOut: (params: CheckOutParams, callbacks?: CheckInActionCallbacks) => Promise<void>;
    dispatchDragMove: (params: DragMoveParams, callbacks: DragMoveCallbacks) => Promise<void>;
}

/**
 * Does the room move and the check-in write, returning the outcome instead of throwing so the caller can
 * report it once. No UI callbacks in here: a callback that throws is not a failed check-in.
 */
const runConfirmArrived = async (
    appointment: DailyAppointment,
    room: string,
    ticket: string
): Promise<CheckinOutcome> => {
    try {
        const [roomUpdated, errorMessage] = await updateAppointment(
            appointment,
            { appointment: { room_id: room } },
            ROOM_CHANGE_ERROR
        );
        if (!roomUpdated) {
            showErrorNotification(errorMessage);
            return { kind: CheckinOutcomeKind.FAILED, reason: 'room_update_failed' };
        }

        if (!(await checkin(appointment, ticket))) {
            showErrorNotification(CHECKIN_ERROR);
            return { kind: CheckinOutcomeKind.FAILED, reason: 'checkin_call_failed' };
        }

        return { kind: CheckinOutcomeKind.CHECKED_IN, ticket: ticket };
    } catch {
        showErrorNotification(CHECKIN_ERROR);
        return { kind: CheckinOutcomeKind.FAILED, reason: 'unexpected_error' };
    }
};

/**
 * Centralised check-in orchestration shared by the check-in button and room-view drag-and-drop.
 * Owns SWR revalidation and the "is checking in" flag; callers supply UI side effects via callbacks.
 */
export const useCheckInActions = (): CheckInActions => {
    const { mutate: mutateAppointmentsForDate } = useAppointmentsForDate({ show_cancelled: false });
    const { mutate: mutateCheckinData } = useCheckInData();
    const { setAppointmentCheckingIn } = useAppointmentCheckinStore();

    const revalidateAppointmentsThenNotify = async (onRevalidated?: () => void): Promise<void> => {
        await mutateAppointmentsForDate();
        onRevalidated?.();
    };

    const settle = (
        appointment: DailyAppointment,
        room: string | undefined,
        callbacks?: CheckInActionCallbacks,
        onAppointmentsRevalidated?: () => void
    ): void => {
        setAppointmentCheckingIn(appointment.id, false);
        if (room !== undefined && room !== getRoomId(appointment)) callbacks?.onRoomChanged?.(room);
        void revalidateAppointmentsThenNotify(onAppointmentsRevalidated);
        void mutateCheckinData();
        revalidateAppointmentFormsCache(appointment.id);
        callbacks?.onSuccess?.();
    };

    const fail = (appointment: DailyAppointment, callbacks?: CheckInActionCallbacks, options?: OnFailOptions): void => {
        setAppointmentCheckingIn(appointment.id, false);
        if (options?.checkInFailedAfterMove) {
            showErrorNotification(appendPatientContext(CHECKIN_ERROR, appointment), undefined, { autoClose: false });
        } else if (options?.notifyFailedMove) {
            showErrorNotification(moveFailedMessage(appointment), undefined, { autoClose: false });
        }
        callbacks?.onFail?.();
    };

    const arriveAndCheckIn = async (
        { appointment, room, checkinData, enrichedPractitionerData, forms }: ArriveParams,
        callbacks?: CheckInActionCallbacks
    ): Promise<void> => {
        setAppointmentCheckingIn(appointment.id, true);
        await handleCheckinMove(
            appointment,
            room,
            checkinData,
            enrichedPractitionerData,
            {
                onSuccess: () => settle(appointment, room, callbacks),
                onFail: (options) => fail(appointment, callbacks, options),
                setAppointmentCheckingIn: setAppointmentCheckingIn,
                mutateCheckinData: mutateCheckinData,
                mutateAppointmentsForDate: mutateAppointmentsForDate,
            },
            'cta',
            forms
        );
    };

    const arriveWithForms = async (
        { appointment, room, enrichedPractitionerData, forms }: ArriveWithFormsParams,
        callbacks?: CheckInActionCallbacks
    ): Promise<void> => {
        setAppointmentCheckingIn(appointment.id, true);
        const outcome = await prepareAndCheckinArrived({
            appointment: appointment,
            selected_room: room,
            forms_for_appointment: forms,
            practitioner: enrichedPractitionerData?.practitioners.find(
                (prac) => prac.id === appointment.professional.id
            ),
            intent: CheckinIntent.ARRIVE,
            entry: 'cta',
        });
        if (outcome.kind === CheckinOutcomeKind.FAILED || outcome.kind === CheckinOutcomeKind.CANCELLED) {
            fail(appointment, callbacks);
        } else {
            settle(appointment, room, callbacks);
        }
    };

    /**
     * Updates the appointment's insurance coverage type then immediately fetches
     * insurance-specific forms and proceeds with check-in — all in one atomic flow.
     * Callers should use `onInsuranceUpdated` to apply the local store update.
     */
    const arriveWithInsuranceUpdate = async (
        { appointment, room, insuranceType, checkinData, enrichedPractitionerData }: ArriveWithInsuranceUpdateParams,
        callbacks?: CheckInActionCallbacks
    ): Promise<void> => {
        setAppointmentCheckingIn(appointment.id, true);

        try {
            await updateAppointmentInsuranceType(appointment, insuranceType);
        } catch {
            showErrorNotification('Fehler beim Aktualisieren der Versicherungsart');
            fail(appointment, callbacks);
            return;
        }

        const updatedAppointment: DailyAppointment = {
            ...appointment,
            data: { ...appointment.data, insurance_coverage_type: insuranceType.toLowerCase() },
        };

        // Notify the caller so it can optimistically update UI state (e.g. patientModalStore)
        // before subsequent SWR revalidations pick up the new appointment from the server.
        callbacks?.onInsuranceUpdated?.(updatedAppointment);

        let forms: FormattedForm[] | undefined;
        try {
            const requiredForms = await getRequiredAppointmentForms(updatedAppointment, getSelectedLocation());
            forms = requiredForms.formatted_forms_for_appointment;
        } catch {
            showErrorNotification(CHECKIN_ERROR);
            fail(appointment, callbacks);
            return;
        }

        // Invalidate the SWR cache so the UI reflects the insurance-specific templates after check-in.
        revalidateAppointmentFormsCache(appointment.id);

        if (forms?.length === 0) {
            await handleCheckinMove(
                updatedAppointment,
                room,
                checkinData,
                enrichedPractitionerData,
                {
                    onSuccess: () => settle(updatedAppointment, room, callbacks),
                    onFail: (options) => fail(updatedAppointment, callbacks, options),
                    setAppointmentCheckingIn: setAppointmentCheckingIn,
                    mutateCheckinData: mutateCheckinData,
                    mutateAppointmentsForDate: mutateAppointmentsForDate,
                },
                'cta',
                forms
            );
        } else {
            const outcome = await prepareAndCheckinArrived({
                appointment: updatedAppointment,
                selected_room: room,
                forms_for_appointment: forms,
                practitioner: enrichedPractitionerData?.practitioners.find(
                    (prac) => prac.id === appointment.professional.id
                ),
                intent: CheckinIntent.ARRIVE,
                entry: 'cta',
            });
            if (outcome.kind === CheckinOutcomeKind.FAILED || outcome.kind === CheckinOutcomeKind.CANCELLED) {
                fail(updatedAppointment, callbacks);
            } else {
                settle(updatedAppointment, room, callbacks);
            }
        }
    };

    const changeRoom = async (
        { appointment, room }: ChangeRoomParams,
        callbacks?: CheckInActionCallbacks
    ): Promise<void> => {
        setAppointmentCheckingIn(appointment.id, true);
        const [success, errorMessage] = await updateAppointment(
            appointment,
            { appointment: { room_id: room } },
            ROOM_CHANGE_ERROR
        );
        if (success) {
            if (room !== getRoomId(appointment)) callbacks?.onRoomChanged?.(room);
            void mutateAppointmentsForDate();
            callbacks?.onSuccess?.();
        } else {
            showErrorNotification(errorMessage);
            callbacks?.onFail?.();
        }
        setAppointmentCheckingIn(appointment.id, false);
    };

    const confirmArrived = async (
        { appointment, room, checkinData }: ConfirmArrivedParams,
        callbacks?: CheckInActionCallbacks
    ): Promise<void> => {
        if (!checkinData) return;
        setAppointmentCheckingIn(appointment.id, true);

        const outcome = await runConfirmArrived(appointment, room, checkinData.number);
        reportCheckinCompletion(appointment.id, 'cta', outcome);

        if (outcome.kind === CheckinOutcomeKind.CHECKED_IN) {
            settle(appointment, room, callbacks);
        } else {
            fail(appointment, callbacks);
        }
    };

    const checkOut = async (
        { appointment, checkinData }: CheckOutParams,
        callbacks?: CheckInActionCallbacks
    ): Promise<void> => {
        if (!checkinData) return;
        setAppointmentCheckingIn(appointment.id, true);
        const ticket = checkinData.number || '';
        try {
            const outcome = await checkoutAppointment(appointment, ticket);
            if (outcome === AppointmentMoveOutcome.SUCCESS) {
                settle(appointment, undefined, callbacks);
            } else if (outcome === AppointmentMoveOutcome.PARTIAL) {
                void mutateAppointmentsForDate();
                void mutateCheckinData();
                fail(appointment, callbacks);
            } else {
                fail(appointment, callbacks);
            }
        } catch {
            fail(appointment, callbacks);
        }
    };

    const dispatchDragMove = async (
        { appointment, sourceColumn, destinationColumn, checkinData, enrichedPractitionerData }: DragMoveParams,
        callbacks: DragMoveCallbacks
    ): Promise<void> => {
        setAppointmentCheckingIn(appointment.id, true);
        try {
            await handleAppointmentMove(
                appointment,
                sourceColumn,
                destinationColumn,
                checkinData,
                enrichedPractitionerData,
                {
                    onSuccess: () => settle(appointment, undefined, undefined, callbacks.onSettled),
                    onFail: (options) => fail(appointment, { onFail: callbacks.onReverted }, options),
                    setAppointmentCheckingIn: setAppointmentCheckingIn,
                    mutateCheckinData: mutateCheckinData,
                    mutateAppointmentsForDate: mutateAppointmentsForDate,
                    acquireCheckinLock: callbacks.acquireCheckinLock,
                }
            );
        } catch (error) {
            logger.error('Drag move failed', { error: error });
            fail(appointment, { onFail: callbacks.onReverted }, { notifyFailedMove: true });
        }
    };

    return {
        arriveAndCheckIn: arriveAndCheckIn,
        arriveWithForms: arriveWithForms,
        arriveWithInsuranceUpdate: arriveWithInsuranceUpdate,
        changeRoom: changeRoom,
        confirmArrived: confirmArrived,
        checkOut: checkOut,
        dispatchDragMove: dispatchDragMove,
    };
};
